import React, { useEffect } from 'react';

import { Box, COLORS, Text } from '@clutter/clean';

import { AddressFragment, RateGroupKindEnum } from '@graphql/platform';

import { clearStoragePlan } from '@root/components/checkout/helpers/moving';
import { RadioCard } from '@root/components/checkout/product_pages/subcomponents/radio_card';
import { StorageCheckoutStepProps } from '@root/components/checkout/types';
import { useTrack } from '@root/initializers/wt';
import { EventSchema } from '@root/resources/wt/types';

import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';

import livingRoomSmallImg from '@images/illustrations/living_room_small.svg';
import stackedBoxesImg from '@images/illustrations/stacked_boxes.svg';

import { CardContents } from '../../subcomponents/card_contents';

export const DESKTOP_PLAN_SELECTOR_SIZE = '356px'; // card height * 3 + gap * 2

type StoragePlanAttachStepProps = StorageCheckoutStepProps & {
  movingExtendedDestinationServiceArea?: boolean;
  endAddress?: Partial<AddressFragment>;
};

export const StoragePlanAttach: React.FC<StoragePlanAttachStepProps> = ({
  onChange,
  scrollToStep,
  values: { skipStoragePlanSizeSelected, currentStorageCustomer },
  movingExtendedDestinationServiceArea,
}) => {
  const track = useTrack({ action: 'click' });
  const trackFunnelEvent = useTrackFunnelEvents();

  const onSelect = () => {
    onChange('commitment', RateGroupKindEnum.Mover);
    onChange('skipStoragePlanSizeSelected', false);
    scrollToStep('next');
  };

  const onSkipStoragePlanSize = () => {
    clearStoragePlan(onChange);
    trackFunnelEvent({
      schema: EventSchema.WWW__StorageAddOnCompleted,
      action: 'click',
      metadata: { selected: false },
    });
    scrollToStep('next');
  };

  const visible =
    !currentStorageCustomer && !movingExtendedDestinationServiceArea;

  useEffect(() => {
    visible &&
      trackFunnelEvent({
        schema: EventSchema.WWW__StorageAddOnViewed,
        action: 'display',
        metadata: {},
      });
  }, [visible, trackFunnelEvent]);

  if (!visible) {
    return null;
  }

  return (
    <Box margin={['48px 0 0', null, '72px 0 0']}>
      <Box>
        <Text.Title size="small" color={COLORS.tealDark}>
          Will you be needing storage?
        </Text.Title>
      </Box>
      <Box margin="4px 0 0">
        <Text.Body color={COLORS.storm}>
          You have the option to store some of your items in one of our secure
          facilities. We will pick up these items during your move at no
          additional charge.
        </Text.Body>
      </Box>
      <Box>
        <Box.Grid
          margin={['24px 0 0', '32px 0 0']}
          gridTemplateColumns={['1fr', '1fr 1fr']}
          gridGap={['12px', '16px']}
        >
          <RadioCard
            name="storagePlanAttach"
            value="true"
            selected={skipStoragePlanSizeSelected === false}
            onChange={() => {
              track({
                objectName: 'storage_add_on_card',
                objectType: 'button',
                label: 'Yes, I’ll use my free storage',
                value: true,
              });
              onSelect();
            }}
          >
            <CardContents
              label="Yes, I’ll use my free storage"
              imgSrc={stackedBoxesImg}
              description={
                <>
                  Take advantage of our free first month offer to store until
                  you’re ready to get your items back.
                </>
              }
              price={null}
            />
          </RadioCard>
          <RadioCard
            name="storagePlanAttach"
            value="false"
            selected={skipStoragePlanSizeSelected === true}
            onChange={() => {
              track({
                objectName: 'storage_add_on_card',
                objectType: 'button',
                label: 'No, I do not need storage',
                value: false,
              });
              onSkipStoragePlanSize();
            }}
          >
            <CardContents
              label="No, I do not need storage"
              imgSrc={livingRoomSmallImg}
              description="We’ll move all your items to your new location."
              price={null}
            />
          </RadioCard>
        </Box.Grid>
      </Box>
    </Box>
  );
};
